import Axios from "axios";

export interface ConversationSummary {
  sessionId: string;
  title: string;
  updatedAt: string;
  messageCount: number;
}

export interface ConversationMessage {
  inputText: string;
  responseText: string;
  createdAt: string;
  conversationId?: string;
}

export async function fetchConversations(
  userId: string,
  page = 0,
  size = 20
): Promise<{ content: ConversationSummary[]; last: boolean }> {
  const response = await Axios.get(
    `${process.env.REACT_APP_BASE_URL?.replace("avatar-core", "avatar-audit")}/conversation-history?userId=${encodeURIComponent(userId)}&page=${page}&size=${size}`
  );
  return response.data?.data || { content: [], last: true };
}

export async function fetchConversationMessages(
  sessionId: string
): Promise<ConversationMessage[]> {
  const response = await Axios.get(
    `${process.env.REACT_APP_BASE_URL?.replace("avatar-core", "avatar-audit")}/conversation-history/${sessionId}/messages`
  );
  return response.data?.data || [];
}

export async function renameConversation(
  sessionId: string,
  title: string
): Promise<void> {
  await Axios.put(`${process.env.REACT_APP_BASE_URL?.replace("avatar-core", "avatar-audit")}/conversation-history/${sessionId}/rename`, {
    title,
  });
}

export async function deleteConversation(sessionId: string): Promise<void> {
  await Axios.delete(`${process.env.REACT_APP_BASE_URL?.replace("avatar-core", "avatar-audit")}/conversation-history/${sessionId}`);
}

export async function createOrUpdateSession(
  sessionId: string,
  userId: string,
  title: string
): Promise<void> {
  await Axios.post(`${process.env.REACT_APP_BASE_URL?.replace("avatar-core", "avatar-audit")}/conversation-history`, {
    sessionId,
    userId,
    title,
  });
}

export interface SaveInteractionInput {
  convId?: string | null;
  inputText: string;
  responseText: string;
  sessionId: string;
  userId: string;
  languageUsed?: string | null;
  responseTime?: number | null;
}

/**
 * Persists one Q&A turn to the audit directly (the same reliable path as session creation),
 * so conversation history no longer depends on the response-service's internal audit hop. The
 * audit's /avatar-interactions endpoint stores the message, creates the rating metric (from
 * convId), and upserts the session — all in one call.
 */
export async function saveInteraction(input: SaveInteractionInput): Promise<void> {
  await Axios.post(`${process.env.REACT_APP_BASE_URL?.replace("avatar-core", "avatar-audit")}/avatar-interactions`, {
    convId: input.convId ?? null,
    inputText: input.inputText,
    responseText: input.responseText,
    sessionId: input.sessionId,
    userId: input.userId,
    languageUsed: input.languageUsed ?? null,
    responseTime: input.responseTime ?? null,
    productType: "",
  });
}

export interface ShareResponse {
  token: string;
  shareUrl: string;
}

export async function shareConversation(
  sessionId: string
): Promise<ShareResponse> {
  const response = await Axios.post(
    `${process.env.REACT_APP_BASE_URL?.replace("avatar-core", "avatar-audit")}/conversation-history/${sessionId}/share`
  );
  return response.data?.data;
}

export async function fetchSharedMessages(
  token: string
): Promise<ConversationMessage[]> {
  const response = await Axios.get(
    `${process.env.REACT_APP_BASE_URL?.replace("avatar-core", "avatar-audit")}/conversation-history/shared/${token}`
  );
  return response.data?.data || [];
}
